import React, { createContext, useState, useCallback, useEffect } from "react";
import { toast } from "react-toastify";

export const UserContext = createContext({
  user: null,
  userType: "client", // ✅ "client" or "student"
  setUser: () => {},
  setUserType: () => {},
  loading: true,
  refreshUser: () => Promise.resolve(),
  logout: () => {},
});

export function UserProvider({ children }) {
  const [user, setUserState] = useState(() => {
    try {
      const raw = localStorage.getItem("user");
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  });

  const [userType, setUserTypeState] = useState(() => {
    try {
      return localStorage.getItem("userType") || "client";
    } catch {
      return "client";
    }
  });

  const [loading, setLoading] = useState(true);

  // Force reset stale sessions and cookies once for all existing users to apply wildcard domain cookies
  useEffect(() => {
    const RESET_KEY = "marsmate-session-reset-v1";
    if (!localStorage.getItem(RESET_KEY)) {
      // Clear frontend session storage
      localStorage.removeItem("user");
      localStorage.removeItem("userType");
      localStorage.removeItem("isLoggedIn");
      localStorage.removeItem("scorecardAccess");
      localStorage.removeItem("scorecardtoken");
      
      // Set the flag to prevent loop
      localStorage.setItem(RESET_KEY, "true");
      
      // Request backend to clear the old host-only cookie
      fetch(`${process.env.REACT_APP_BACKEND_URL}/auth/logout`, {
        method: "POST",
        credentials: "include",
      }).catch(() => {});
      
      // Reload to ensure clean React state and fetch new clean session
      window.location.reload();
    }
  }, []);

  // Helper to update state and localStorage together
  const setUser = useCallback((u) => {
    setUserState(u);
    try {
      if (u) localStorage.setItem("user", JSON.stringify(u));
      else localStorage.removeItem("user");
    } catch (e) {
      // ignore
    }
  }, []);

  const setUserType = useCallback((type) => {
    setUserTypeState(type);
    try {
      localStorage.setItem("userType", type);
    } catch (e) {
      // ignore
    }
  }, []);

  // ── Session-expired event handler ──────────────────────────────────────
  // Fired by apiFetch() whenever ANY API call returns 401 Unauthorized.
  useEffect(() => {
    const handleSessionExpired = () => {
      setUser(null);
      // Store message in sessionStorage so login modal picks it up
      const msg = "⚠️ Your session has expired. Please sign in again.";
      sessionStorage.setItem("oauthLoginMessage_client", msg);
      sessionStorage.setItem("oauthLoginMessage_student", msg);
      toast.error("Your session has expired. Please sign in again.", {
        toastId: "session-expired", // prevents duplicate toasts
        autoClose: 7000,
      });
    };
    window.addEventListener("session-expired", handleSessionExpired);
    return () => window.removeEventListener("session-expired", handleSessionExpired);
  }, [setUser]);

  const refreshUser = useCallback(async (overrideUserType = null) => {
    try {
      // Use the provided userType override, or fall back to current state
      //const effectiveUserType = overrideUserType !== null ? overrideUserType : userType;

      // Try to fetch from the general /auth/me endpoint first (works after Google OAuth)
      let res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/auth/me`, {
        credentials: "include",
      });

      // If /auth/me succeeds, user is a client (even if we thought they were student)
      if (res.ok) {
        const data = await res.json();
        const fetchedUser = data.user || null;

        // Auto-detect and update userType based on response
        if (fetchedUser) {
          const detectedType = fetchedUser.role === "student" ? "student" : "client";
          if (detectedType !== userType) {
            setUserType(detectedType);
          }
        }

        setUser(fetchedUser);
        setLoading(false);
        return fetchedUser;
      }

      // If /auth/me fails, always try /student/me as fallback (handles Google OAuth students)
      res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/student/me`, {
        credentials: "include",
      });

      if (res.ok) {
        const data = await res.json();
        // Auto-detect and set userType to student
        if (userType !== "student") {
          setUserType("student");
        }
        setUser(data.user || null);
        setLoading(false);
        return data.user || null;
      }

      // ── Both endpoints failed ────────────────────────────────────────────
      // Check if we had a stale user in localStorage — if so, the cookie
      // has expired and the user needs to re-authenticate.
      const hadStoredUser = Boolean(localStorage.getItem("user"));

      // Always clear stale frontend state
      setUser(null);
      localStorage.removeItem("user");
      localStorage.removeItem("userType");
      localStorage.removeItem("isLoggedIn");
      localStorage.removeItem("scorecardAccess");
      localStorage.removeItem("scorecardtoken");
      setLoading(false);

      // If the user *was* logged in (stale data existed) and the session is
      // now gone, show the expiry toast and redirect to login.
      if (hadStoredUser) {
        const isAlreadyOnHome = window.location.pathname === "/";
        const msg = "⚠️ Your session has expired. Please sign in again.";

        if (!isAlreadyOnHome) {
          // Save where they were so we can redirect after re-login
          sessionStorage.setItem("postLoginRedirect", window.location.pathname + window.location.search);
          localStorage.setItem("postLoginRedirect", window.location.pathname + window.location.search);
        }

        // Write to both login form sessionStorage keys — message appears
        // inside the modal right where the user is looking
        sessionStorage.setItem("oauthLoginMessage_client", msg);
        sessionStorage.setItem("oauthLoginMessage_student", msg);

        toast.error("Your session has expired. Please sign in again.", {
          toastId: "session-expired",
          autoClose: 7000,
        });
        if (!isAlreadyOnHome) {
          // Small delay so the toast renders before navigation
          setTimeout(() => {
            window.location.href = "/?openLogin=true&sessionExpired=true";
          }, 300);
        }
      }

      return null;
    } catch (err) {
      console.error("refreshUser error:", err);
      setUser(null);
      setLoading(false);
      return null;
    }
  }, [setUser, userType, setUserType]);

  const logout = useCallback(async () => {
    setUser(null);
    localStorage.removeItem("user");
    localStorage.removeItem("userType");
    
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch (err) {
      console.error("Logout error", err);
    }
  }, [setUser]);

  useEffect(() => {
    let mounted = true;

    if (!user) {
      (async () => {
        await refreshUser();
      })();
    } else {
      if (mounted) setLoading(false);
    }

    return () => {
      mounted = false;
    };
  }, [user, refreshUser]);

  return (
    <UserContext.Provider value={{ user, userType, setUser, setUserType, loading, refreshUser, logout }}>
      {children}
    </UserContext.Provider>
  );
}